// UltraFlex Member Arrears — Payment Success/Verify Landing
// Called by Stripe after successful checkout with ?session_id=...
// Polls /api/dd/me/arrears/verify until payment_status is confirmed.
import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Loader2, CheckCircle2, XCircle, RefreshCw, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MemberArrearsSuccess() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState('loading'); // loading | pending | success | error
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState(null);
  const [attempts, setAttempts] = useState(0);

  const params = new URLSearchParams(location.search);
  const sessionId = params.get('session_id');

  const verify = useCallback(async () => {
    if (!sessionId) { setState('error'); setError('Missing session_id in URL'); return; }
    try {
      const res = await fetch(
        `${API_URL}/api/dd/me/arrears/verify?session_id=${encodeURIComponent(sessionId)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      let data;
      try { data = await res.json(); }
      catch { data = { detail: res.statusText || 'Server error' }; }
      if (!res.ok) throw new Error(data.detail || 'Verification failed');
      setPayload(data);
      if (data.status === 'success' || data.status === 'already_processed') {
        setState('success');
      } else if (data.payment_status === 'unpaid' || data.status === 'pending') {
        setState('pending');
      } else {
        setState('success');  // treat unknown as complete
      }
    } catch (e) { setState('error'); setError(e.message); }
  }, [sessionId, token]);

  useEffect(() => {
    verify();
    // Poll up to 6 times over ~18s while Stripe finalises
    const timer = setInterval(() => {
      setAttempts(a => {
        if (a >= 5) { clearInterval(timer); return a; }
        verify();
        return a + 1;
      });
    }, 3000);
    return () => clearInterval(timer);
  }, [verify]);

  return (
    <div className="max-w-2xl mx-auto p-4 md:p-10" data-testid="arrears-success-page">
      {state === 'loading' && (
        <div className="bg-uf-surface border border-uf-border rounded-xl p-8 text-center">
          <Loader2 className="w-10 h-10 animate-spin text-uf-red mx-auto mb-3" />
          <h1 className="text-xl font-semibold text-white mb-1">Confirming your payment…</h1>
          <p className="text-sm text-uf-text-muted">This usually takes just a few seconds.</p>
        </div>
      )}

      {state === 'pending' && (
        <div className="bg-amber-900/20 border border-amber-800 rounded-xl p-8 text-center" data-testid="pending-panel">
          <Loader2 className="w-10 h-10 animate-spin text-amber-400 mx-auto mb-3" />
          <h1 className="text-xl font-semibold text-white mb-1">Waiting for bank confirmation</h1>
          <p className="text-sm text-uf-text-muted mb-4">Your payment is still being processed by your bank. This page will refresh automatically.</p>
          <button onClick={verify} data-testid="retry-verify"
            className="inline-flex items-center gap-2 px-4 py-2 bg-uf-surface border border-uf-border text-white rounded-lg text-sm hover:bg-white/5">
            <RefreshCw className="w-4 h-4" /> Check again
          </button>
        </div>
      )}

      {state === 'success' && payload && (
        <div className="bg-emerald-900/20 border border-emerald-800 rounded-xl p-8 text-center" data-testid="success-panel">
          <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
          <h1 className="text-2xl font-bold text-white mb-1">Payment received</h1>
          <p className="text-sm text-emerald-200 mb-4">
            Thanks — we&apos;ve cleared £{Number(payload.total_cleared ?? 0).toFixed(2)} of arrears and set up your new Direct Debit.
          </p>
          <div className="text-left inline-block bg-black/30 border border-emerald-900 rounded-lg p-4 text-sm mb-6 space-y-1">
            <div className="flex justify-between gap-8">
              <span className="text-uf-text-muted">Status</span>
              <span className={payload.member_status === 'active' ? 'text-emerald-400 font-medium' : 'text-amber-400 font-medium'}>
                {payload.member_status === 'active' ? 'Reinstated' : payload.member_status}
              </span>
            </div>
            {payload.new_mandate_id && (
              <div className="flex justify-between gap-8">
                <span className="text-uf-text-muted">New mandate</span>
                <span className="text-white font-mono text-xs">{payload.new_mandate_id.slice(0, 8)}…</span>
              </div>
            )}
          </div>
          {payload.member_status === 'active' ? (
            <Link to="/hub" data-testid="hub-link"
              className="inline-flex items-center gap-2 px-5 py-3 bg-uf-red hover:bg-uf-red-hover text-white rounded-lg font-semibold">
              Return to Member Hub <ChevronRight className="w-4 h-4" />
            </Link>
          ) : (
            <p className="text-xs text-amber-400 mt-2">
              {payload.reinstate_reason || 'Your account will be reactivated shortly by our team.'}
            </p>
          )}
        </div>
      )}

      {state === 'error' && (
        <div className="bg-red-900/20 border border-red-800 rounded-xl p-8 text-center" data-testid="error-panel">
          <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
          <h1 className="text-xl font-semibold text-white mb-1">We couldn&apos;t confirm your payment</h1>
          <p className="text-sm text-red-300 mb-4">{error}</p>
          <p className="text-xs text-uf-text-muted mb-4">
            Your card may not have been charged. Please retry or contact us if you were charged and haven&apos;t been reinstated.
          </p>
          <div className="flex justify-center gap-2">
            <button onClick={verify} data-testid="retry-btn"
              className="px-4 py-2 bg-uf-surface border border-uf-border text-white rounded-lg text-sm">Retry</button>
            <button onClick={() => navigate('/member/arrears')}
              className="px-4 py-2 bg-uf-red text-white rounded-lg text-sm">Back to arrears</button>
          </div>
        </div>
      )}
    </div>
  );
}
